import React, { useContext, useState } from 'react';

import { invalidatePostQueries } from 'common/actions/postQueries';
import { invalidatePosts, reloadPost } from 'common/actions/posts';
import { invalidatePostActivities } from 'common/actions/postsActivity';
import { reloadRoadmapPostsForRoadmap } from 'common/actions/roadmapPosts';
import AJAX from 'common/AJAX';
import { RoadmapsContext } from 'common/containers/RoadmapsContainer';
import connect from 'common/core/connect';
import parseAPIResponse, { isDefaultSuccessResponse } from 'common/util/parseAPIResponse';

import GenericActionModal, { GenericActionError } from './GenericActionModal';
import GenericSuggestion from './GenericSuggestion';
import { findStringMatchesByName } from './utils';

import type { CommonModalProps } from './index';
import type { Roadmap } from 'common/api/endpoints/roadmaps';
import type { Post } from 'common/api/resources/posts';
import type { Dispatch } from 'redux-connect';

type OwnProps = CommonModalProps;

type ConnectProps = {
  invalidatePostData: (posts: Post[]) => Promise<void>;
  reloadPost: (post: Post) => Promise<void>;
  reloadRoadmapPostsForRoadmap: (roadmap: Roadmap) => Promise<void>;
};

type Props = OwnProps & ConnectProps;

const AddToRoadmapModal = ({
  currentRoadmap,
  invalidatePostData,
  onClose,
  reloadPost,
  reloadRoadmapPostsForRoadmap,
  selectedPosts,
  selectedPost,
}: Props) => {
  const { roadmaps } = useContext(RoadmapsContext);
  const [searchValue, setSearchValue] = useState<string>('');

  const handleSubmit = async (selectedRoadmap: Roadmap) => {
    const responseJSON = await AJAX.post('/api/posts/bulkEdit', {
      roadmapID: selectedRoadmap._id,
      operationType: 'addToRoadmap',
      postIDs: selectedPosts.map((post) => post._id),
    });
    const { error } = parseAPIResponse(responseJSON, {
      isSuccessful: isDefaultSuccessResponse,
    });

    if (error) {
      throw new GenericActionError({ message: error.message, source: 'AddToRoadmap' });
    } else {
      const postsToInvalidate = selectedPosts.filter((post) => post._id !== selectedPost?._id); // only invalidate posts that aren't the selected post
      await Promise.all([
        invalidatePostData(postsToInvalidate),
        selectedPost && reloadPost(selectedPost),
        reloadRoadmapPostsForRoadmap(selectedRoadmap),
      ]);
    }
  };

  // remove the roadmap we're currently viewing and any archived roadmaps
  const filteredRoadmaps = (roadmaps ?? [])
    .filter((roadmap) => !roadmap.archived)
    .filter((roadmap) => roadmap._id !== currentRoadmap._id);

  const postPlural = selectedPosts.length > 1 ? 'posts' : 'post';

  return (
    <GenericActionModal<Roadmap>
      cta={`Add ${postPlural}`}
      onClose={onClose}
      onSearchValueChanged={(searchValue) => setSearchValue(searchValue)}
      onSubmit={handleSubmit}
      searchValue={searchValue}
      showSearch={filteredRoadmaps.length > 10}
      successMessage={`Successfully added ${selectedPosts.length} ${postPlural} to roadmap`}
      suggestions={findStringMatchesByName(filteredRoadmaps, searchValue)}
      SuggestionComponent={GenericSuggestion}
      suggestionsLoading={false}
      title={`Add ${postPlural} to roadmap`}
    />
  );
};

export default connect(null, (dispatch: Dispatch) => ({
  invalidatePostData: (posts: Post[]) => {
    return Promise.all([
      dispatch(invalidatePostActivities(posts)),
      dispatch(invalidatePostQueries()),
      dispatch(invalidatePosts(posts)),
    ]);
  },
  reloadPost: (post: Post) => dispatch(reloadPost(post)),
  reloadRoadmapPostsForRoadmap: (roadmap: Roadmap) =>
    dispatch(reloadRoadmapPostsForRoadmap(roadmap)),
}))(AddToRoadmapModal) as unknown as React.FC<OwnProps>;
